import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAppointmentProviders,
  qPatientById,
  qPrescriptionPrintSettings,
  qPrescriptionsByEncounter,
  qUserProviderLink,
  qVitalsByEncounterId,
  qEncounterById,
  resolvePrintLetterhead,
  formatVitalsSummary,
  buildPrintVitals,
  type AppConfigRow,
  type AppointmentProviderRow,
  type PatientDetailRow,
  type PatientEncounterRow,
  type PatientPrescriptionRow,
  type PatientVitalEntryWithDefinitionRow,
  type PrescriptionPrintSettingsRow,
  type UserProviderLinkRow,
  applyPrintPurpose,
  type PrescriptionPrintPurpose,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  PrescriptionPrintLayout,
  type PrintPrescriptionItem,
} from '../clinic/prescription-print/PrescriptionPrintLayout';
import { printImageDataUrl } from '../clinic/prescription-print/print-utils';
import { PrintProgressToast } from '../../components/PrintProgressToast';
import { renderNodeToPng } from '../../lib/prescription-image';
import { useEncounterPrintClinical } from '../clinic/prescription-print/useEncounterPrintClinical';
import {
  formatPatientAge,
  resolveDoctor,
  resolvePrintSettings,
} from '../clinic/prescription-print/print-utils';
import { useBrandTokens } from '../../shell/shared';

// PRD.202 — drop-in mount that lets Pharmacy contexts print the existing
// encounter prescription document directly (no navigation hop).
//
// Implementation strategy: render the prescription layout to static HTML
// via `react-dom/server`, push that HTML into a hidden iframe, then call
// `iframe.contentWindow.print()`. The iframe is fully isolated from the
// host page — its own @page rule, its own visibility — so the host
// page's tab state, scroll position, and CSS context cannot be affected
// by the print in any way (this is the cause of the earlier "goes to
// prescription tab" symptom: the browser's print snapshot was capturing
// the host page rather than a clean isolated surface).
//
// No DB writes happen here — pure read + browser print.

export interface PrescriptionPrintRequest {
  patientId: string;
  encounterId: string;
  /**
   * PRD.327 — what this printout is for.
   *
   * `consultation_sheet` (the default) renders the clinic's saved settings
   * exactly, which is what every caller did before the option existed.
   * `prescription` runtime-suppresses vitals and the clinical note so the sheet
   * is safe to hand to a pharmacy. Same template, same settings record, nothing
   * persisted.
   */
  purpose?: PrescriptionPrintPurpose;
}

export function PrescriptionPrintMount({
  request,
  onAfterPrint,
}: {
  request: PrescriptionPrintRequest | null;
  onAfterPrint: () => void;
}) {
  const { user, claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const patientId = request?.patientId ?? '';
  const encounterId = request?.encounterId ?? '';

  const { data: rxRows = [] } = usePowerSyncQuery<PatientPrescriptionRow>(
    qPrescriptionsByEncounter(encounterId),
  );
  const { data: printSettingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: providerLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  // PRD.284 — encounter vitals + chief complaint (rendered only when the print
  // settings' Vitals / Complaint sections are enabled).
  const { data: vitalsRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    qVitalsByEncounterId(encounterId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId),
  );

  const printSettings = useMemo(
    () =>
      // PRD.327 — the purpose is a RUNTIME view of the saved settings. Applied
      // here, so every caller of this mount gets it without a second layout,
      // and the clinic's stored configuration is never written to.
      applyPrintPurpose(
        resolvePrintSettings(printSettingsRows[0] ?? null, appConfigRows[0] ?? null),
        request?.purpose,
      ),
    [printSettingsRows, appConfigRows, request?.purpose],
  );
  // PRD.284 — clinic-wide print font color + letterhead branding.
  const printBranding = useMemo(
    () => resolvePrintLetterhead(appConfigRows[0] ?? null),
    [appConfigRows],
  );
  const printVitalsSummary = useMemo(() => formatVitalsSummary(vitalsRows), [vitalsRows]);
  const printComplaint = encounterRows[0]?.chief_complaint ?? '';

  // PRD.320 — Dental Structured clinical content. Composed from canonical
  // encounter domains via the shared hook, so this path can never diverge from
  // the wizard preview. Other templates ignore these props.
  // The encounter id comes straight from the REQUEST, not from
  // `encounterRows[0]?.id`. Going through that row meant the clinical
  // composition stayed disabled (encounterId `''` → every query no-ops → empty
  // summary) until an extra lookup resolved — so a print fired before then had
  // no Clinical Note at all.
  const printClinical = useEncounterPrintClinical(encounterId, patientId, printComplaint);
  const printVitals = useMemo(() => buildPrintVitals(vitalsRows), [vitalsRows]);
  const printDoctor = useMemo(() => {
    const linkedProviderId = providerLinkRows[0]?.provider_id ?? null;
    const linkedProvider = linkedProviderId
      ? (providerRows.find((p) => p.id === linkedProviderId) ?? null)
      : null;
    return resolveDoctor(linkedProvider, user?.displayName ?? null);
  }, [providerLinkRows, providerRows, user?.displayName]);
  const printPatient = useMemo(() => {
    const p = patientRows[0];
    return {
      name: p?.full_name ?? '',
      age: formatPatientAge(p), // PRD.315 — falls back to reported age
      sex: p?.sex ?? '',
    };
  }, [patientRows]);
  const printItems: PrintPrescriptionItem[] = useMemo(
    () =>
      rxRows.map((r) => {
        const snapshotDetails = [
          r.brand_name_snapshot,
          r.generic_name_snapshot,
          r.strength_snapshot,
          r.dosage_form_snapshot,
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          itemName: r.item_name_snapshot ?? r.free_text_name ?? r.item_name ?? 'Untitled medicine',
          itemDetails: r.item_details ?? snapshotDetails,
          intakeTiming: r.intake_timing ?? '',
          doseQuantity: r.dose_quantity ?? r.default_dosage_snapshot ?? '',
          scheduleMorning: r.schedule_morning === 1,
          scheduleAfternoon: r.schedule_afternoon === 1,
          scheduleNight: r.schedule_night === 1,
          durationValue: r.duration_value ?? null,
          durationUnit: r.duration_unit ?? '',
          instructions: r.instructions ?? r.default_instruction_snapshot ?? '',
        };
      }),
    [rxRows],
  );

  /**
   * The request already sent to the printer, compared by IDENTITY.
   *
   * It used to be a `patientId:encounterId` string, which caused two
   * intermittent dead clicks:
   *
   *  - the key ignored `purpose`, so Consultation Sheet followed by
   *    Prescription on the same visit looked like a repeat and was skipped;
   *  - the key was only cleared when `onAfterPrint` fired, and that depends on
   *    the browser's `afterprint` event — whose fallback is a 60s watchdog. Any
   *    click in that window silently did nothing.
   *
   * Each click creates a fresh request object, so identity is an exact "is this
   * a new request" test and neither failure can recur.
   */
  const lastFiredRequest = useRef<PrescriptionPrintRequest | null>(null);
  // Visible feedback while data syncs and the sheet rasterizes. Without it the
  // action looked like it did nothing on a slow sync.
  const [rasterizing, setRasterizing] = useState(false);
  const [stalled, setStalled] = useState<string | null>(null);

  // Wait until ALL the data we need to render the prescription is loaded.
  // PowerSync queries each resolve independently — without this gate, the
  // print can fire after `printItems` arrives but before the patient row
  // does, producing a printout with blank Patient Name / Age cells.
  const patientLoaded = patientRows.length > 0;
  /**
   * Whether there is enough to print.
   *
   * A PRESCRIPTION with no medication is meaningless, so it still requires
   * items. A CONSULTATION SHEET is not: it is the visit document, and a visit
   * with vitals, findings and a diagnosis but no medication is completely
   * ordinary. Requiring items for both was silently turning that case into a
   * dead click.
   */
  const requiresItems = request?.purpose === 'prescription';
  const itemsLoaded = !requiresItems || printItems.length > 0;
  const encounterLoaded = encounterRows.length > 0;

  const printNodeRef = useRef<HTMLDivElement>(null);
  // Stable per print, so the offscreen node and the captured image cannot
  // disagree about the date.
  const printDate = useMemo(() => new Date().toISOString(), [request?.encounterId]);
  const printTitle = request?.purpose === 'prescription' ? 'Prescription' : 'Consultation Sheet';

  useEffect(() => {
    if (!request) {
      lastFiredRequest.current = null;
      setRasterizing(false);
      setStalled(null);
      return;
    }
    // A new object means a new click, whatever the previous one did.
    if (lastFiredRequest.current === request) return;

    // The node is rasterized ONCE, so whatever has arrived at that instant is
    // what prints. The Clinical Note comes from nine
    // separate clinical queries, and an empty summary is ambiguous — it means
    // both "still loading" and "nothing recorded". Gating on the composition's
    // own `loaded` flag is what stops a Consultation Sheet printing without its
    // Clinical Note when opened straight from the Visit Note menu.
    // Still gathering. The user sees the "Preparing…" overlay rather than a
    // button that appears to do nothing, and the effect simply re-runs when the
    // data lands — so waiting is the normal path, not a failure.
    if (!patientLoaded || !itemsLoaded || !encounterLoaded || !printClinical.loaded) {
      // A long backstop for the genuinely stuck case — offline, or a visit with
      // no medication when a Prescription was asked for. It reports in the
      // overlay instead of an alert, so it cannot interrupt anything.
      const t = setTimeout(() => {
        setStalled(
          requiresItems && printItems.length === 0
            ? 'No medication on this visit. Add items in the Prescriptions tab, or print the Consultation Sheet instead.'
            : 'Still loading this visit. Check your connection and try again.',
        );
      }, 12000);
      return () => clearTimeout(t);
    }

    lastFiredRequest.current = request;
    setStalled(null);
    setRasterizing(true);

    // Rasterize the offscreen node and print THAT image — the same pipeline the
    // encounter Prescriptions tab uses, so both surfaces produce an identical
    // sheet. It also inherits that path's iPad fix: a self-contained bitmap
    // re-renders identically when the print sheet reloads on a settings change,
    // whereas a live-HTML iframe goes blank.
    let cancelled = false;
    void (async () => {
      const node = printNodeRef.current;
      if (!node) {
        onAfterPrint();
        return;
      }
      try {
        const png = await renderNodeToPng({ node });
        if (cancelled) return;
        setRasterizing(false);
        if (png.dataUrl) {
          printImageDataUrl(png.dataUrl, printTitle, onAfterPrint);
        } else {
          onAfterPrint();
        }
      } catch (err) {
        console.error('[prescription-print] image render failed', err);
        setRasterizing(false);
        setStalled('Could not prepare this printout. Please try again.');
        onAfterPrint();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    request,
    patientLoaded,
    encounterLoaded,
    printClinical.loaded,
    itemsLoaded,
    printItems,
    printSettings,
    printBranding,
    printComplaint,
    printVitalsSummary,
    printDoctor,
    printPatient,
    primary,
    onAfterPrint,
  ]);

  // The offscreen node the rasterizer captures. Rendered only while a print is
  // in flight, and laid out (not `display: none`) so it has real geometry.
  if (!request) return null;
  return (
    <>
      <PrintProgressToast
        message={rasterizing ? `Preparing ${printTitle}…` : `Loading visit for ${printTitle}…`}
        error={stalled}
        onDismiss={onAfterPrint}
      />

      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: -100000,
          top: 0,
          width: '210mm',
          background: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        <div ref={printNodeRef}>
          <PrescriptionPrintLayout
            settings={printSettings}
            doctor={printDoctor}
            patient={printPatient}
            date={printDate}
            items={printItems}
            complaint={printComplaint}
            vitalsSummary={printVitalsSummary}
            clinical={printClinical}
            vitals={printVitals}
            accentColor={primary}
            fontColor={printBranding.fontColor}
            letterhead={printBranding.letterhead}
          />
        </div>
      </div>
    </>
  );
}
